import { useMemo } from 'react';
import { Source, Destination, OptimizelyProduct } from '../../../../../../types/DataSyncItem';
import { DataSyncObjectsStore } from '../../../../../../stores/object/DataSyncObjectsStore';
import ProductStoreFactory from '../../../../../../stores/product/ProductStoreFactory';

interface HeaderInfo {
  name: string;
  iconUrl?: string;
}

interface FieldMappingHeaderInfo {
  sourceInfo: HeaderInfo;
  destinationInfo: HeaderInfo;
}

/**
 * Custom hook to calculate source and destination header info for field mapping table
 *
 * Logic:
 * - For CGR/CMP: Use product name and icon from product stores
 * - For HUB/HUB_APP: Use selected instance/object name and icon
 */
export const useFieldMappingHeaderInfo = (
  source: Source,
  destination: Destination,
  sourceStore: DataSyncObjectsStore | null,
  destinationStore: DataSyncObjectsStore | null,
  storesLoaded: boolean
): FieldMappingHeaderInfo => {
  const sourceProductStore = ProductStoreFactory.getSourceProductStore();
  const destinationProductStore = ProductStoreFactory.getDestinationProductStore();

  const sourceInfo = useMemo<HeaderInfo>(() => {
    if (
      !storesLoaded ||
      !source ||
      !source.product ||
      sourceProductStore.hubAppsLoading ||
      sourceProductStore.productInstancesLoading
    ) {
      return { name: 'Source Field' };
    }

    // Get product info from product store
    const productInfo = sourceProductStore.getProduct(source.product);

    if (!productInfo) {
      return { name: 'Source Field' };
    }

    switch (source.product) {
      case OptimizelyProduct.CGR:
      case OptimizelyProduct.CMP:
        // For CGR and CMP, use product name and icon
        return {
          name: productInfo.text,
          iconUrl: productInfo.iconPath,
        };

      case OptimizelyProduct.HUB:
      case OptimizelyProduct.HUB_APP: {

        const instance = sourceProductStore.getInstance(source.product, source.instance.id);

        // For HUB and HUB_APP, use the selected object/app name and icon
        if (!sourceStore) {
          // Fallback to product info if no object selected
          return {
            name: productInfo.text,
            iconUrl: productInfo.iconPath,
          };
        }

        const objectDefinition = sourceStore.getSourceObjectDefinition(
          source.object.object_name
        );

        return {
          name:
            instance?.text ||
            source.object.object_display_name ||
            source.object.object_name,
          iconUrl:
            instance?.iconPath ||
            source.object.object_icon_url ||
            objectDefinition?.object_icon_url ||
            productInfo.iconPath,
        };
      }

      default:
        return {
          name: productInfo.text,
          iconUrl: productInfo.iconPath,
        };
    }
  }, [
    source,
    sourceStore,
    storesLoaded,
    sourceProductStore.hubAppsLoading,
    sourceProductStore.hubApps,
    sourceProductStore.productInstancesLoading,
  ]);

  const destinationInfo = useMemo<HeaderInfo>(() => {
    if (
      !storesLoaded ||
      !destination ||
      !destination.product ||
      destinationProductStore.hubAppsLoading ||
      destinationProductStore.productInstancesLoading
    ) {
      return { name: 'Destination Field' };
    }

    // Get product info from product store
    const productInfo = destinationProductStore.getProduct(destination.product);

    if (!productInfo) {
      return { name: 'Destination Field' };
    }

    switch (destination.product) {
      case OptimizelyProduct.CGR:
      case OptimizelyProduct.CMP:
        // For CGR and CMP, use product name and icon
        return {
          name: productInfo.text,
          iconUrl: productInfo.iconPath,
        };

      case OptimizelyProduct.HUB:
      case OptimizelyProduct.HUB_APP: {

        const instance = destinationProductStore.getInstance(destination.product, destination.instance.id);
        // For HUB and HUB_APP, use the selected object/app name and icon
        if (!destinationStore) {
          // Fallback to product info if no object selected
          return {
            name: productInfo.text,
            iconUrl: productInfo.iconPath,
          };
        }

        const objectDefinition =
          destinationStore.getDestinationObjectDefinition(
            destination.object.object_name
          );

        return {
          name:
            instance?.text ||
            objectDefinition?.object_display_name ||
            destination.object.object_name,
          iconUrl:
            instance?.iconPath ||
            destination.object.object_icon_url ||
            objectDefinition?.object_icon_url ||
            productInfo.iconPath,
        };
      }

      default:
        return {
          name: productInfo.text,
          iconUrl: productInfo.iconPath,
        };
    }
  }, [
    destination,
    destinationStore,
    storesLoaded,
    destinationProductStore.hubAppsLoading,
    destinationProductStore.hubApps,
    destinationProductStore.productInstancesLoading,
  ]);

  return {
    sourceInfo,
    destinationInfo,
  };
};
